import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckAndTransferOwnershipSteps, InitializationSteps } from './types';
import { IdentityInstance } from '@/utils/identity-utils/identity-contract-module';
import { IPortal } from '@/types';
import { KeyStoreInstance } from '@/utils/identity-utils/key-store';
import { DynamicAlertBanner } from '../alert-banners/DynamicAlertBanner';
import { LucideIcon, cn } from '@fileverse/ui';

interface LegacyFlowProps {
  startFrom: InitializationSteps | CheckAndTransferOwnershipSteps;
  portalKeys: IPortal;
  onCompleteSetup: (flow?: 'legacy' | 'new') => void;
}

export const LegacyUserFlow = ({
  startFrom,
  portalKeys,
  onCompleteSetup,
}: LegacyFlowProps) => {
  const [initializationSteps, setInitializationSteps] = useState(startFrom);
  const isRunningRef = useRef(false);
  const [isError, setIsError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [checkAndTransferOwnershipSteps, setCheckAndTransferOwnershipSteps] =
    useState(CheckAndTransferOwnershipSteps.NOT_INITIALIZED);

  const updateInitializationSteps = (step: InitializationSteps) => {
    setInitializationSteps(step);
  };

  const updateCheckAndTransferOwnershipSteps = (
    step: CheckAndTransferOwnershipSteps
  ) => {
    setCheckAndTransferOwnershipSteps(step);
  };

  const setupLegacyUserIdentity = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;

    try {
      setIsError(false); // Reset error state before attempting

      switch (startFrom) {
        case InitializationSteps.SETUP_NEW_IDENTITY_MODULE:
          await IdentityInstance.setupNewIdentityModule();
          setInitializationSteps(
            InitializationSteps.CHECK_AND_TRANSFER_OWNERSHIP
          );

          await KeyStoreInstance.setupLegacyUserKeyStore(
            updateInitializationSteps,
            updateCheckAndTransferOwnershipSteps,
            portalKeys
          );
          break;

        case InitializationSteps.CHECK_AND_TRANSFER_OWNERSHIP:
          await KeyStoreInstance.setupLegacyUserKeyStore(
            updateInitializationSteps,
            updateCheckAndTransferOwnershipSteps,
            portalKeys
          );
          break;
      }
      onCompleteSetup();
    } catch (error) {
      setIsError(true);
      console.error('Error during legacy user setup:', error);
    } finally {
      isRunningRef.current = false;
    }
  }, [startFrom, onCompleteSetup, portalKeys]);

  useEffect(() => {
    setupLegacyUserIdentity();
  }, [setupLegacyUserIdentity]);

  const getCurrentStep = () => {
    if (initializationSteps === InitializationSteps.SETUP_NEW_IDENTITY_MODULE) {
      return { step: '1 / 3', title: 'Setting up new identity' };
    } else if (
      initializationSteps === InitializationSteps.CHECK_AND_TRANSFER_OWNERSHIP
    ) {
      switch (checkAndTransferOwnershipSteps) {
        case CheckAndTransferOwnershipSteps.NOT_INITIALIZED:
          return {
            step: '2 / 3',
            title: 'Checking and transferring ownership',
          };
        case CheckAndTransferOwnershipSteps.REGISTER_NEW_OWNER_AS_COLLABORATOR:
          return {
            step: '2 / 3',
            title: 'Registering new owner as collaborator',
          };
        case CheckAndTransferOwnershipSteps.REGISTER_COLLABORATOR_KEYS:
          return { step: '2 / 3', title: 'Registering collaborator keys' };
        case CheckAndTransferOwnershipSteps.TRANSFER_OWNERSHIP:
          return { step: '2 / 3', title: 'Transferring ownership' };
        case CheckAndTransferOwnershipSteps.ACCEPT_OWNERSHIP:
          return { step: '2 / 3', title: 'Accepting ownership' };
        default:
          return { step: '2 / 3', title: 'Checking ownership' };
      }
    } else {
      return { step: '3 / 3', title: 'Updating keystore' };
    }
  };

  const stepInfo = getCurrentStep();

  return (
    <DynamicAlertBanner
      hideCloseIcon={true}
      icon={
        <div className="flex items-center justify-center px-[9px] py-[5px] rounded color-bg-secondary">
          <span className="text-heading-lg">{isError ? '👩‍🔬' : '👩‍🏭'}</span>
        </div>
      }
      iconAlignment="center"
      title={isError ? 'Oops, something went wrong' : 'Upgrading your account'}
      description={
        <div className="flex flex-col">
          {!isError ? (
            <>
              <p>
                Your dDocs App is getting a super upgrade for better performance
                and reliability. This should take no more than 40 seconds.
              </p>
              <p>
                Learn more{' '}
                <a
                  href="/infra-upgrade"
                  target="_blank"
                  className="color-text-link hover:underline"
                >
                  here!
                </a>
              </p>
            </>
          ) : retryCount >= 3 ? (
            <p>
              If the issue persists, team Fileverse is here for you! Please
              reach out to us with a small description of the bug.
            </p>
          ) : null}
        </div>
      }
      footer={
        isError ? (
          <>
            <div className="border-t color-border-default pt-2 px-3 flex justify-between items-center">
              <div className="flex flex-col">
                <p className="text-helper-text-sm color-text-secondary">
                  Migrating to a new architecture
                  <span className="animate-[dots_1s_ease-in-out_infinite]">
                    ...
                  </span>
                </p>
                <div className="text-helper-text-sm color-text-secondary">
                  <span className="flex flex-col gap-1">
                    <p className="color-text-danger">{stepInfo.title}</p>
                  </span>
                </div>
              </div>
              {retryCount < 3 ? (
                <span>
                  <button
                    className="flex justify-center items-center gap-1 color-text-default"
                    onClick={() => {
                      setRetryCount((prev) => prev + 1);
                      window.location.reload();
                    }}
                  >
                    <LucideIcon name="RotateCw" size="sm" />
                    Retry
                  </button>
                </span>
              ) : (
                <span>
                  <span className="color-text-danger">{stepInfo.step[0]}</span>
                  {stepInfo.step.slice(1)}
                </span>
              )}
            </div>
            {retryCount >= 3 && (
              <div className="border-t color-border-default">
                <div className="flex justify-center items-center color-text-default text-helper-text-sm-bold">
                  <button
                    className="w-full h-[46px] flex justify-center items-center gap-1 hover:color-bg-secondary-hover transition-all border-r color-border-default"
                    onClick={() => {
                      setRetryCount((prev) => prev + 1);
                      window.location.reload();
                    }}
                  >
                    <LucideIcon name="RotateCw" size="sm" />
                    Retry
                  </button>
                  <a
                    href="mailto:feedback@fileverse.io"
                    className="w-full h-[46px] flex justify-center items-center gap-1 hover:color-bg-secondary-hover transition-all"
                  >
                    <LucideIcon name="Mail" size="sm" />
                    Contact us
                  </a>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="border-t color-border-default pt-2 px-3">
            <div className="flex justify-between items-center text-helper-text-sm color-text-secondary">
              <span className="flex flex-col gap-1">
                <p>
                  Migrating to a new architecture
                  <span className="animate-[dots_1s_ease-in-out_infinite]">
                    ...
                  </span>
                </p>
                <p>{stepInfo.title}</p>
              </span>
              <span>
                <span className="color-text-default">{stepInfo.step[0]}</span>
                {stepInfo.step.slice(1)}
              </span>
            </div>
          </div>
        )
      }
      variant="default"
      className={cn(
        'max-w-[320px] border color-border-default shadow-elevation-4 flex flex-col gap-2 !px-0',
        retryCount >= 3 && isError && '!pb-0'
      )}
      contentClassName="px-3"
      isLoading={!isError}
    />
  );
};
